import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

// Same fixed-tenant convention used throughout (AppContext.tsx etc.) --
// this DMS instance is single-tenant today.
const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

/**
 * Reads which modules a superadmin has enabled for this tenant (see
 * tenant_modules / SuperadminDashboard.tsx's module toggles). A module
 * with no row at all reads as disabled -- the same "nothing's on until a
 * superadmin turns it on" convention SuperadminDashboard's own toggle UI
 * uses, so a brand-new tenant's dashboard and the superadmin panel that
 * configures it never disagree about what "unset" means. TGB already has
 * an explicit row (is_enabled = true) for all 10 catalog modules, so
 * nothing changes for the one tenant that exists today.
 */
export function useTenantModules() {
  const [enabledByModuleKey, setEnabledByModuleKey] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('tenant_modules')
        .select('module_key, is_enabled')
        .eq('tenant_id', DEFAULT_TENANT_ID);
      if (cancelled) return;
      if (error) {
        console.error('Failed to load tenant modules:', error);
        // Fail open -- a load error should never silently hide every
        // gated tab for a real admin session; see isModuleEnabled below.
        setLoaded(true);
        return;
      }
      const map: Record<string, boolean> = {};
      (data || []).forEach((row: { module_key: string; is_enabled: boolean }) => {
        map[row.module_key] = row.is_enabled;
      });
      setEnabledByModuleKey(map);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** True if the module is enabled, if module gating hasn't finished
   *  loading yet or failed to load (fail open -- never hide a tab because
   *  of a slow network or a transient error), or if moduleKey is
   *  null/undefined (an intentionally ungated tab). Only reads as false
   *  once loading has genuinely completed and found no enabled row. */
  const isModuleEnabled = (moduleKey: string | null | undefined): boolean => {
    if (!moduleKey) return true;
    if (!loaded) return true;
    return enabledByModuleKey[moduleKey] ?? false;
  };

  return { isModuleEnabled, loaded };
}
